import { addDays } from 'date-fns'
import {
  AtestadoSdcDto,
  ExamesSdcDto,
  ProcedimentoSdcDto,
  ReceitaMedicamentoSdcDto,
  UnidadeSaude,
} from 'graphql/types.generated'
import { CidadaoAtendimentoContextModel } from 'hooks/atendimento-context/model-atendimentoContext'
import { dateAsDdMmYyyy, dateAsYyyyMmDd, formatDateHourMinutes } from 'util/date/formatDate'
import { v4 as uuidv4 } from 'uuid'

import AtestadoModel from '../atestados/components/model-atestado'
import { buildAtestadoPadraoText } from '../atestados/padrao/utils-atestadoPadrao'
import { ExameListSelectFieldModel } from '../exames/components/ExameListSelectField'
import { SolicitacaoExameModel } from '../exames/model-solicitacaoExames'
import {
  MedicamentoFormModel,
  TipoDoseEnum,
  TipoMotivoPrescricaoEnum,
} from '../prescricao-medicamento/model-prescricao'

interface MotivoPrescricaoSdc {
  tipoMotivoPrescricao?: TipoMotivoPrescricaoEnum
  cidMotivoPrescricao?: { id: string; codigo: string; nome: string } | null
  ciapMotivoPrescricao?: { id: string; codigo: string; descricao: string } | null
}

const parseMotivoPrescricao = (
  prescricao: ReceitaMedicamentoSdcDto,
  isMotivoPrescricaoEnabled: boolean
): MotivoPrescricaoSdc => {
  if (!isMotivoPrescricaoEnabled || !prescricao.tipoMotivoPrescricao) return {}

  const isCid = prescricao.tipoMotivoPrescricao === 'CID10'
  const isCiap = prescricao.tipoMotivoPrescricao === 'CIAP2'

  if (!isCid && !isCiap) return {}

  return {
    tipoMotivoPrescricao: isCid ? TipoMotivoPrescricaoEnum.CID10 : TipoMotivoPrescricaoEnum.CIAP2,
    cidMotivoPrescricao: isCid
      ? {
          id: prescricao.idMotivoPrescricao,
          codigo: prescricao.codigoMotivoPrescricao,
          nome: prescricao.nomeMotivoPrescricao,
        }
      : null,
    ciapMotivoPrescricao: isCiap
      ? {
          id: prescricao.idMotivoPrescricao,
          codigo: prescricao.codigoMotivoPrescricao,
          descricao: prescricao.nomeMotivoPrescricao,
        }
      : null,
  }
}

export const converterReceitaMedicamentoSdcDtoToMedicamentoFormModel = (
  prescricao: ReceitaMedicamentoSdcDto,
  isMotivoPrescricaoEnabled: boolean
): MedicamentoFormModel => {
  const dataInicioTratamento = new Date()
  const dataFimTratamento = addDays(dataInicioTratamento, prescricao.duracaoTratamentoEmDias)
  const motivoPrescricao = parseMotivoPrescricao(prescricao, isMotivoPrescricaoEnabled)

  return (
    prescricao.prescrever && {
      id: prescricao.medicamentoCatmat?.medicamento.id,
      dataInicioTratamento: dateAsYyyyMmDd(dataInicioTratamento),
      dataFimTratamento: dateAsYyyyMmDd(dataFimTratamento),
      duracao: prescricao.duracaoTratamentoEmDias,
      escalaDuracao: prescricao.medidaTempoFrequencia,
      intervaloDose: prescricao.intervaloFrequencia,
      tipoFrequencia: prescricao.tipoFrequenciaDose,
      tipoDose: TipoDoseEnum.COMUM,
      isEditFirstLoad: false,
      viaAdministracao: {
        id: prescricao.aplicacaoMedicamento.id,
        nome: prescricao.aplicacaoMedicamento.nome,
      },
      posologia: prescricao.posologia,
      qtDose: prescricao.quantidadeDose,
      unidadeFornecimento: prescricao.medicamentoCatmat.medicamento.unidadeFornecimento,
      recomendacoes: prescricao.recomendacoes,
      quantidade: prescricao.quantidadeReceitada,
      principioAtivoCombo: prescricao.medicamentoCatmat,
      unidadeMedidaDose: prescricao.unidadeMedidaDose,
      ...motivoPrescricao,
    }
  )
}

export const converterExameSdcToSolicitacaoExameModel = (
  examesSdc: ExamesSdcDto,
  mapExamesParaPrescricao: Map<string, ProcedimentoSdcDto>
): SolicitacaoExameModel => {
  const listaExames = Array.from(mapExamesParaPrescricao.values())
  const examesSolicitados: ExameListSelectFieldModel[] = listaExames.map(converterExameCondutaToExameFormModel)

  return {
    _id: uuidv4(),
    tipoExame: examesSdc.tipoExame,
    examesSolicitados: examesSolicitados,
    justificativa: examesSdc.justificativa,
    cid10: examesSdc.cid10,
  }
}

const converterExameCondutaToExameFormModel = ({
  exameSolicitado: exame,
}: ProcedimentoSdcDto): ExameListSelectFieldModel => ({
  _id: uuidv4(),
  exame: {
    id: exame.id,
    codigo: exame.codigo,
    descricao: exame.descricao,
    idadeMaxima: exame.idadeMaxima,
    idadeMinima: exame.idadeMinima,
    procedimentoReferencia: exame.procedimentoReferencia,
  },
})

export const converterAtestadoSdcToAtestadoModel = (
  atestado: AtestadoSdcDto,
  unidadeSaude: UnidadeSaude,
  cidadao: CidadaoAtendimentoContextModel,
  iniciadoEm: Date
): AtestadoModel => ({
  tipo: atestado.tipoAtestado,
  descricao: buildAtestadoPadraoText({
    nomeUnidadeSaude: unidadeSaude.nome,
    cidadao: cidadao,
    data: dateAsDdMmYyyy(iniciadoEm),
    hora: formatDateHourMinutes(iniciadoEm),
    cid10: atestado.cid10,
    dias: atestado.dias,
  }),
  cid10: atestado.cid10,
  data: dateAsYyyyMmDd(iniciadoEm) as any,
  dias: atestado.dias,
  hora: formatDateHourMinutes(iniciadoEm),
})
